import {
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow
} from '@material-ui/core'
import { Copy, Settings, Trash2 } from 'lucide-react'
import { useEffect, useState } from 'react'
import TableRowSkeleton from '../../components/TableRowSkeleton'
import { useStyles } from './style'

const QuickTable = ({
  data = [],
  columns = [],
  loading = false,
  actions = [],
  onEdit = null,
  onCopy = null,
  onRoles = null,
  onDelete = null,
  onRowClick = null,
  customRowClass = null,
  containerProps = {},
  tableProps = {},
  skeletonRows = 3,
  emptyMessage = 'Nenhum item encontrado',
  customCellRenderer = null,
  customActionsRenderer = null,
  showAlternatingRows = true
}) => {
  const classes = useStyles()
  const [tableData, setTableData] = useState([])

  useEffect(() => {
    if (Array.isArray(data)) {
      setTableData(data)
    }
  }, [data])

  const handleEdit = (item, event) => {
    event.stopPropagation()
    if (onEdit) onEdit(item)
  }

  // const handleRoles = (item, event) => {
  //   event.stopPropagation()
  //   if (onRoles) onRoles(item)
  // }

  const handleCopy = (item, event) => {
    event.stopPropagation()
    if (onCopy) onCopy(item)
  }

  const handleDelete = (item, event) => {
    event.stopPropagation()
    if (onDelete) onDelete(item)
  }

  const handleRowClick = item => {
    if (onRowClick) onRowClick(item)
  }

  const getRowClassName = (index, item) => {
    if (customRowClass) {
      return customRowClass(item, index)
    }
    if (showAlternatingRows) {
      return index % 2 === 0 ? classes.tableRowEven : classes.tableRowOdd
    }
    return ''
  }

  const renderCellContent = (item, column) => {
    if (customCellRenderer && customCellRenderer[column.field]) {
      return customCellRenderer[column.field](item)
    }

    // Handle nested properties with dot notation (e.g., 'user.name')
    if (column.field.includes('.')) {
      const fields = column.field.split('.')
      let value = item
      for (const field of fields) {
        value = value && value[field]
      }
      return value
    }

    return item[column.field]
  }

  const renderActions = item => {
    if (customActionsRenderer) {
      return customActionsRenderer(item)
    }

    // Resolve se a prop actions é um array fixo ou uma função dinâmica por linha
    const allowedActions =
      typeof actions === 'function' ? actions(item) : actions

    return (
      <>
        {/* CORRETO: Usa allowedActions */}
        {allowedActions.includes('copy') && onCopy && (
          <IconButton
            size="small"
            onClick={e => handleCopy(item, e)}
            className={classes.buttonActionEdit}
            aria-label="Copiar item"
          >
            <Copy size={20} />
          </IconButton>
        )}

        {/* CORRIGIDO: Alterado de 'actions' para 'allowedActions' */}
        {allowedActions.includes('edit') && onEdit && (
          <IconButton
            size="small"
            onClick={e => handleEdit(item, e)}
            className={classes.buttonActionEdit}
            aria-label="Editar item"
          >
            <Settings size={20} />
          </IconButton>
        )}

        {/* CORRIGIDO: Alterado de 'actions' para 'allowedActions' */}
        {allowedActions.includes('delete') && onDelete && (
          <IconButton
            size="small"
            onClick={e => handleDelete(item, e)}
            className={classes.buttonActionDelete}
            aria-label="Excluir item"
          >
            <Trash2 size={20} />
          </IconButton>
        )}
      </>
    )
  }

  const hasActions =
    (typeof actions === 'function' || actions.length > 0) &&
    (onEdit || onCopy || onRoles || onDelete || customActionsRenderer)

  return (
    <Paper
      {...containerProps}
      className={`${classes.tableContainer} ${containerProps.className || ''}`}
      elevation={0}
    >
      <Table size="small" {...tableProps} className={classes.table}>
        <TableHead>
          <TableRow>
            {columns.map(column => (
              <TableCell
                key={column.field}
                align={column.align || 'left'}
                className={classes.tableCellTitle}
                style={column.headerStyle || {}}
              >
                {column.headerName}
              </TableCell>
            ))}
            {hasActions && (
              <TableCell align="center" className={classes.tableCellTitle}>
                Ações
              </TableCell>
            )}
          </TableRow>
        </TableHead>
        <TableBody>
          {loading ? (
            <TableRowSkeleton
              columns={hasActions ? columns.length + 1 : columns.length}
              rows={skeletonRows}
            />
          ) : tableData.length > 0 ? (
            tableData.map((item, index) => (
              <TableRow
                key={item.id || index}
                className={
                  typeof customRowClass === 'function' &&
                  typeof customRowClass(item, index) === 'string'
                    ? customRowClass(item, index)
                    : ''
                }
                onClick={() => handleRowClick(item)}
                style={{
                  cursor: onRowClick ? 'pointer' : 'default',
                  ...(typeof customRowClass === 'function' &&
                  typeof customRowClass(item, index) === 'object'
                    ? customRowClass(item, index)
                    : {})
                }}
              >
                {columns.map(column => (
                  <TableCell
                    key={`${item.id || index}-${column.field}`}
                    align={column.align || 'left'}
                    className={classes.tableCellMessage}
                    style={column.style || {}}
                  >
                    {renderCellContent(item, column)}
                  </TableCell>
                ))}
                {hasActions && (
                  <TableCell
                    align="center"
                    className={classes.tableCellMessage}
                  >
                    {renderActions(item)}
                  </TableCell>
                )}
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell
                colSpan={hasActions ? columns.length + 1 : columns.length}
                align="center"
                className={classes.tableCellMessage}
              >
                {emptyMessage}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </Paper>
  )
}

// QuickTable.propTypes = {
//   data: PropTypes.array,
//   columns: PropTypes.arrayOf(
//     PropTypes.shape({
//       field: PropTypes.string.isRequired,
//       headerName: PropTypes.string.isRequired,
//       align: PropTypes.oneOf(['left', 'center', 'right']),
//       headerStyle: PropTypes.object,
//       style: PropTypes.object
//     })
//   ).isRequired,
//   loading: PropTypes.bool,
//   actions: PropTypes.arrayOf(PropTypes.oneOf(['edit', 'delete', 'custom'])),
//   onEdit: PropTypes.func,
//   onDelete: PropTypes.func,
//   onRowClick: PropTypes.func,
//   customRowClass: PropTypes.func,
//   containerProps: PropTypes.object,
//   tableProps: PropTypes.object,
//   skeletonRows: PropTypes.number,
//   emptyMessage: PropTypes.node,
//   customCellRenderer: PropTypes.object,
//   customActionsRenderer: PropTypes.func,
//   showAlternatingRows: PropTypes.bool
// }

export default QuickTable
